/**
 * dcent-drag-scroll.js — PC 마우스로 가로 스크롤 영역을 끌어서 넘긴다 (MAGE-581).
 *
 * 터치에는 브라우저가 이미 스와이프를 주지만 마우스에는 없다. 그래서 PC 에서는 화살표 버튼이
 * 있는 자리만 넘길 수 있었고, 버튼이 없는 목록(공지 트랙·칩 줄·리뷰 스크롤러 등)은 손이 닿지 않았다.
 *
 * ── 설계 ──────────────────────────────────────────────────────────────────
 * · **위임 한 벌**로 문서 전체를 맡는다. 가로 스크롤 컨테이너가 13곳이고 JS 로 나중에 그려지는
 *   목록(코인 행 등)도 있어서, 요소를 미리 찾아 붙이는 방식이면 그때그때 놓친다.
 * · 대상 판정은 **실측**이다 — computed overflow-x 가 auto|scroll 이고 실제로 넘쳐 있어야 한다.
 *   미디어쿼리로 모바일에서만 스크롤이 되는 자리는 PC 에서 자동으로 대상에서 빠진다.
 * · `pointerType === 'mouse'` 만 가로챈다. 터치·펜은 브라우저 기본 동작이 이미 낫다.
 * · 문턱(5px)을 넘겨야 '끌기'로 보고, 끌었으면 **바로 뒤의 click 한 번을 삼킨다** —
 *   카드 위에서 끌기 시작했을 때 링크가 열리면 안 된다.
 * · 세로 의도(|dy| > |dx|)로 시작하면 손을 뗀다. 페이지 세로 스크롤을 뺏지 않기 위해서다.
 * · scroll-snap 은 끄는 동안만 해제한다. 켠 채로 끌면 스냅이 손가락과 싸운다. 놓는 순간
 *   되돌리므로 가장 가까운 카드로 붙는 마무리는 그대로 남는다.
 * · 전용 CSS 를 만들지 않는다 — 끄는 동안의 커서·드래그 방지는 인라인 스타일로 걸고 뗀다.
 *   자산 하나 더 로드하는 값이 규칙 두 줄보다 크다.
 *
 * ── 제외 ──────────────────────────────────────────────────────────────────
 *   폼 컨트롤 · contenteditable · 자체 포인터 드래그를 쓰는 비교 슬라이더(dcent-image-compare) ·
 *   `[data-dcent-no-drag]` 를 단 자리.
 */

const MIN_OVERFLOW = 8; // 이만큼은 넘쳐야 '스크롤되는 자리'로 본다
const DRAG_THRESHOLD = 5; // 이만큼 움직여야 끌기로 본다
const SKIP = "input, textarea, select, option, [contenteditable], .dcent-image-compare, [data-dcent-no-drag]";

function scrollableAncestor(start) {
  let el = start instanceof Element ? start : null;
  while (el && el !== document.body && el !== document.documentElement) {
    if (el.matches(SKIP)) return null;
    const overflowX = getComputedStyle(el).overflowX;
    if (
      (overflowX === "auto" || overflowX === "scroll") &&
      el.scrollWidth - el.clientWidth > MIN_OVERFLOW
    ) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/* 끌기 직후의 click 한 번만 막는다. 타이머로 거두면 click 과 순서가 갈려 새어 나가므로,
 * 플래그를 두고 **다음 click 에서 스스로 꺼진다.** 끌지 않은 pointerdown 이 플래그를 먼저
 * 지우므로, 끌었는데 click 이 오지 않은 경우(빈 자리에서 놓음)에도 남의 클릭을 먹지 않는다. */
let suppressClick = false;

if (typeof document !== "undefined") {
  document.addEventListener(
    "click",
    (e) => {
      if (!suppressClick) return;
      suppressClick = false;
      e.preventDefault();
      e.stopPropagation();
    },
    true,
  );
}

export function initDragScroll(root = document) {
  root.addEventListener("pointerdown", (e) => {
    if (e.pointerType !== "mouse" || e.button !== 0) return;
    suppressClick = false; // 이전 끌기의 잔재가 남의 클릭을 먹지 않게
    const el = scrollableAncestor(e.target);
    if (!el) return;

    const startX = e.clientX;
    const startY = e.clientY;
    const startLeft = el.scrollLeft;
    let dragging = false;
    let abandoned = false;
    let prevSnap = "";
    let prevUserSelect = "";
    let prevCursor = "";

    const begin = () => {
      dragging = true;
      prevSnap = el.style.scrollSnapType;
      el.style.scrollSnapType = "none";
      prevUserSelect = document.body.style.userSelect;
      prevCursor = document.body.style.cursor;
      document.body.style.userSelect = "none";
      document.body.style.cursor = "grabbing";
    };

    /* 네이티브 드래그를 끄는 구간 — 이걸 안 막으면 **이미지·링크 위에서 시작한 끌기가 아예 안 된다.**
     * <img>·<a> 는 기본으로 draggable 이라 마우스를 누른 채 움직이면 브라우저가 HTML5 드래그를
     * 시작하고, 그 순간부터 pointermove 가 끊긴다(2026-08-30 실측: 추천 상품 트랙만 안 움직였는데
     * 시작점이 카드 이미지였다. 글자로 된 블로그 칩 줄은 잘 됐다).
     * pointerdown 에서 preventDefault 로 막지 않는 이유: 그러면 포커스·텍스트 선택까지 통째로
     * 죽는다. dragstart 만 이 상호작용 동안 잡아 두고 손을 떼면 원상복구한다. */
    const onDragStart = (de) => de.preventDefault();

    const onMove = (ev) => {
      const dx = ev.clientX - startX;
      const dy = ev.clientY - startY;
      if (!dragging) {
        if (abandoned) return;
        if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) return;
        // 세로가 더 크면 페이지 스크롤 의도다 — 넘긴다
        if (Math.abs(dy) > Math.abs(dx)) {
          abandoned = true;
          return;
        }
        begin();
      }
      el.scrollLeft = startLeft - dx;
      ev.preventDefault();
    };

    const onUp = () => {
      window.removeEventListener("dragstart", onDragStart);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      if (!dragging) return;
      el.style.scrollSnapType = prevSnap;
      document.body.style.userSelect = prevUserSelect;
      document.body.style.cursor = prevCursor;
      // 끌고 난 직후의 click 을 삼킨다(카드 위에서 끌었을 때 링크가 열리지 않게).
      suppressClick = true;
    };

    window.addEventListener("dragstart", onDragStart);
    window.addEventListener("pointermove", onMove, { passive: false });
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  });
}

if (typeof window !== "undefined" && typeof document !== "undefined") {
  initDragScroll();
}
